/**
 * Pet stat registry — SEPARATE from CREATURE_DEFS by design.
 *
 * A tamed pet shares the wire entity + mesh with its wild counterpart (it's a
 * Creature with ownerId), but its NUMBERS come from here, not from the wild
 * CREATURE_DEFS row. Wild stats scale by gameDuration; pet stats scale by the
 * pet's own LEVEL (separate counter). KTS source: gf_UpdatePetStats
 * (MapScript.galaxy:27996). Day/night atk-speed shift at :28181-28186.
 *
 * Roster: docs/design/17-pets.md §"v1 pet roster".
 * Decision: docs/superpowers/specs/2026-06-18-pets-implementation-design.md §2.1.
 */
import type { CreatureType } from "./creatures";

export type PetSpecies = "Bear" | "Wolf" | "Lyote" | "Nightwisp";

export interface PetDef {
  species: PetSpecies;
  /** Wire creatureType reused for mesh/render (client resolves mesh off this). */
  creatureType: CreatureType;
  baseDamage: number;
  baseLife: number;
  damagePerLevel: number;
  lifePerLevel: number;
  /** Attack-speed multiplier in the favored phase; 1 = no shift.
      Wolf favors day (phase 0); Lyote favors night (phase 1); Bear neither. */
  dayAtkSpeedMult: number;
  nightAtkSpeedMult: number;
  attackRangeU: number;
}

/** Pet level cap — KTS-faithful: gf_AddExperience gates at level 3 (MapScript.galaxy:5570). */
export const PET_LEVEL_CAP = 3;

const BEAR_PET: PetDef = {
  species: "Bear", creatureType: "Bear",
  baseDamage: 8, baseLife: 125, damagePerLevel: 1.2, lifePerLevel: 15,
  dayAtkSpeedMult: 1, nightAtkSpeedMult: 1, attackRangeU: 0.5,
};
// KTS unit "Dog" — daytime-themed: x1.25 atk-speed during day (gf_UpdatePetStats:28182).
const WOLF_PET: PetDef = {
  species: "Wolf", creatureType: "Wolf",
  baseDamage: 10, baseLife: 100, damagePerLevel: 1.5, lifePerLevel: 11,
  dayAtkSpeedMult: 1.25, nightAtkSpeedMult: 1, attackRangeU: 0.5,
};
// Nighttime-themed: x1.35 atk-speed during night (gf_UpdatePetStats:28185).
const LYOTE_PET: PetDef = {
  species: "Lyote", creatureType: "Lyote",
  baseDamage: 10, baseLife: 100, damagePerLevel: 1.5, lifePerLevel: 11,
  dayAtkSpeedMult: 1, nightAtkSpeedMult: 1.35, attackRangeU: 0.5,
};

// Ranged caster — NightwispBeamMissile, 4.5u magic (17-pets.md §"v1 pet roster").
// Base 4 +1/lvl dmg, 85 +9/lvl life. No day/night atk-speed shift.
const NIGHTWISP_PET: PetDef = {
  species: "Nightwisp", creatureType: "Nightwisp",
  baseDamage: 4, baseLife: 85, damagePerLevel: 1, lifePerLevel: 9,
  dayAtkSpeedMult: 1, nightAtkSpeedMult: 1, attackRangeU: 4.5,
};

export const PET_DEFS: Record<PetSpecies, PetDef> = {
  Bear: BEAR_PET, Wolf: WOLF_PET, Lyote: LYOTE_PET, Nightwisp: NIGHTWISP_PET,
};

export function isPetSpecies(t: string): t is PetSpecies {
  return t === "Bear" || t === "Wolf" || t === "Lyote" || t === "Nightwisp";
}

/** Pet species that fire from range (vs walk-to-melee). v1: Nightwisp only. */
export const RANGED_PET_SPECIES: ReadonlyArray<PetSpecies> = ["Nightwisp"];
export function isRangedPet(t: string): boolean {
  return (RANGED_PET_SPECIES as ReadonlyArray<string>).includes(t);
}

// ---------------------------------------------------------------------------
// Pet mastery upgrades — data-only (shared so the client can render the panel)
//
// Cube-mutating helpers (hasUpgrade/grantUpgrade/canAfford/debitCost) remain
// in game/server/src/pets/mastery.ts (server-only concerns).
//
// Costs and effects verbatim from docs/design/06-economy-gear.md §PetUpgrades_Func
// and KTS GameStrings.txt Button/Tooltip/* strings.
// ---------------------------------------------------------------------------

/** Cube.mods key for the 7-bit mastery bitmask. Single source of truth. */
export const MASTERY_MOD_KEY = "taughtPets" as const;

export interface UpgradeDef {
  /** Unique string id (matches the upgrade name from 06-economy-gear.md). */
  id: string;
  /** Bit position [0–6] in the `taughtPets` bitmask. */
  bit: number;
  /** ResourceBag field key for the material cost ("leather" | "bearPelt"). */
  materialId: "leather" | "bearPelt";
  /** How many of the material is required. */
  materialQty: number;
  /** Gold fallback price when the owner lacks the material. */
  goldCost: number;
  /** Short human-readable label shown in the mastery panel. */
  label: string;
  /** One-line effect summary shown under the label (from KTS tooltip strings). */
  blurb: string;
}

/**
 * All 7 mastery upgrades in bit order [0..6].
 * Costs verbatim from docs/design/06-economy-gear.md:124-132.
 *
 * Leather group (bits 0–2): 1 Leather or 20g
 * BearPelt group (bits 3–5): 1 BearPelt or 30g
 * Loyalty (bit 6): 2 BearPelt or 50g
 */
export const PET_UPGRADES: ReadonlyArray<UpgradeDef> = [
  {
    id: "ThickHide", bit: 0, materialId: "leather", materialQty: 1, goldCost: 20,
    label: "Thick Hide",
    blurb: "Pets gain +2 armor and +1 life regen (+3 additional regen out of combat).",
  },
  {
    id: "InspirationalMaster", bit: 1, materialId: "leather", materialQty: 1, goldCost: 20,
    label: "Inspirational Master",
    blurb: "Pets gain passive XP per second; Sabertooths & Direwolves +3 dmg/level, Cave Bears +2 dmg +1 armor/level, Nightwisps +2 dmg/level.",
  },
  {
    id: "NaturalTenacity", bit: 2, materialId: "leather", materialQty: 1, goldCost: 20,
    label: "Natural Tenacity",
    blurb: "Pets dodge certain boss abilities; -30% damage from wild animals and ghouls; +5 spell armor.",
  },
  {
    id: "Companionship", bit: 3, materialId: "bearPelt", materialQty: 1, goldCost: 30,
    label: "Companionship",
    blurb: "Pets L2+: +4 attack damage and +15% attack speed; both doubled when master is below 35% HP.",
  },
  {
    id: "PredatoryKnowledge", bit: 4, materialId: "bearPelt", materialQty: 1, goldCost: 30,
    label: "Predatory Knowledge",
    blurb: "Pets L2+: +10% HP; Sabertooths & Direwolves charge for +25% first-attack damage; Cave Bears +2 HP regen + nearby allied kobolds +1 regen; Nightwisps: every 3rd attack +5 magic dmg + restores 3 energy.",
  },
  {
    id: "Viciousness", bit: 5, materialId: "bearPelt", materialQty: 1, goldCost: 30,
    label: "Viciousness",
    blurb: "Pets L2+: +10% movement speed and ignore 25% of target armor on attacks.",
  },
  {
    id: "Loyalty", bit: 6, materialId: "bearPelt", materialQty: 2, goldCost: 50,
    label: "Loyalty",
    blurb: "Pets L3+: respawn with full life at the nearest allied Hunter's Lodge on death (180s cooldown per pet).",
  },
] as const;

// Quick lookup map (id → def)
const UPGRADE_BY_ID = new Map<string, UpgradeDef>(PET_UPGRADES.map(u => [u.id, u]));

/** Resolve an upgrade by id string. Returns undefined for unknown ids. */
export function findUpgrade(id: string): UpgradeDef | undefined {
  return UPGRADE_BY_ID.get(id);
}

// ---------------------------------------------------------------------------
// Trophy data — data-only (shared so the client can render the trophy submenu)
//
// Store/deposit/revoke helpers remain in game/server/src/pets/trophies.ts.
// Effect values from docs/design/17-pets.md §trophies (NOT KTS runtime which had
// different values — the design doc is the locked spec).
// ---------------------------------------------------------------------------

export type TrophyType = "Antlers" | "WhiteBearFang" | "BoneTalisman";

export interface TrophyDef {
  /** Trophy type (also used as the backpack itemId). */
  type: TrophyType;
  /** Armor bonus added to armorBonus accumulator. */
  armorBonus: number;
  /** Flat damage bonus added to the additive damage accumulator (before Maille ×). */
  damageBonus: number;
  /** FLAT HP bonus added AFTER maxLifeMult multiply (not a fraction). */
  hpFlatBonus: number;
  /** Short human-readable label shown in the trophy submenu. */
  label: string;
  /** One-line effect summary. */
  blurb: string;
}

export const TROPHIES: ReadonlyArray<TrophyDef> = [
  {
    type: "Antlers",       armorBonus: 0.5, damageBonus: 0, hpFlatBonus: 0,
    label: "Antlers",
    blurb: "Deposit at this lodge to grant pets +0.5 armor.",
  },
  {
    type: "WhiteBearFang", armorBonus: 0,   damageBonus: 1, hpFlatBonus: 0,
    label: "Cave Bear Fang",
    blurb: "Deposit at this lodge to grant pets +1 damage.",
  },
  {
    type: "BoneTalisman",  armorBonus: 0,   damageBonus: 0, hpFlatBonus: 10,
    label: "Bone Talisman",
    blurb: "Deposit at this lodge to grant pets +10 HP.",
  },
] as const;

// Quick lookup by type
const TROPHY_BY_TYPE = new Map<TrophyType, TrophyDef>(TROPHIES.map(t => [t.type, t]));

export function getTrophyDef(type: TrophyType): TrophyDef | undefined {
  return TROPHY_BY_TYPE.get(type);
}
